import { CircleTopicFragment } from '@modernhealth/circles-graphql/v2';

import { captureException } from 'src/shared/helpers/captureException';

/**
 * Sorts a list of topic edges from `fetchSeriesWithSessions` and returns a limited subset
 *
 * @param topics an array of topic edges to sort and filter
 * @param numberOfTopics the number of topics to return
 */
export function sortTopicsByFirstSession<
    T extends {
        node: CircleTopicFragment;
    },
>(topics: (T | null)[], numberOfTopics?: number): CircleTopicFragment[] {
    const sortedTopics = topics
        .flatMap(topicEdge => {
            // Filter out any null/empty nodes
            if (!topicEdge?.node || !topicEdge.node.sessions.edges[0]?.node) return [];

            const firstSession = topicEdge.node.sessions.edges[0].node;
            const firstSessionTime = new Date(firstSession.startTime).getTime();

            // Sort out series with invalid times
            if (Number.isNaN(firstSessionTime)) {
                captureException(new Error(`Invalid time in session`), {
                    sessionId: firstSession.id,
                    startTime: firstSession.startTime,
                });

                return [];
            }

            return {
                topic: topicEdge.node,
                firstSessionTime,
            };
        })
        // Sort by the first session time
        .sort(({ firstSessionTime: a }, { firstSessionTime: b }) => a - b);

    const mappedTopics = sortedTopics.map(({ topic }) => topic);

    if (typeof numberOfTopics !== 'number') return mappedTopics;

    return (
        // Select a subset of topics
        mappedTopics.slice(0, numberOfTopics)
    );
}
